// ProactiveChatBubble - Bulle d'aide proactive pour le chatbot
// Apparaît après 10 secondes sur les sections pack Déménagement/Héritage
// Inclut infos sur les alertes et FAQ missions
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Bot, Home, Shield, Bell, ChevronRight } from 'lucide-react';

const ProactiveChatBubble = ({ onOpenChat, variant = 'landing' }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [hasTriggered, setHasTriggered] = useState(false);
  const [currentTip, setCurrentTip] = useState(0);

  // Tips rotatifs pour le dashboard
  const dashboardTips = [
    {
      title: "Activez les alertes !",
      description: "Recevez des notifications en temps réel sur l'avancement de vos résiliations et les économies réalisées.",
      icon: Bell,
      color: "text-yellow-400",
      bgColor: "bg-yellow-400/10"
    },
    {
      title: "Mission Déménagement",
      description: "Je gère la résiliation de vos anciens contrats et l'ouverture des nouveaux à votre nouvelle adresse.",
      icon: Home,
      color: "text-cyan-400",
      bgColor: "bg-cyan-400/10"
    },
    {
      title: "Mission Succession",
      description: "Je vous accompagne dans la clôture des contrats d'un proche disparu avec bienveillance et efficacité.",
      icon: Shield,
      color: "text-magenta-400",
      bgColor: "bg-magenta-400/10"
    }
  ];

  useEffect(() => {
    // Vérifier si déjà dismissé dans cette session
    const dismissed = sessionStorage.getItem('chatBubbleDismissed');
    if (dismissed) {
      setIsDismissed(true);
      return;
    }

    let timeoutId;
    let observerCleanup;
    let rotationInterval;

    if (variant === 'landing') {
      // Observer pour détecter si l'utilisateur reste sur la section pricing
      const pricingSection = document.getElementById('pricing');
      
      if (pricingSection) {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach(entry => {
              if (entry.isIntersecting && !hasTriggered && !isDismissed) {
                // Déclencher après 10 secondes sur la section
                timeoutId = setTimeout(() => {
                  setIsVisible(true);
                  setHasTriggered(true);
                }, 10000);
              } else {
                // Annuler si l'utilisateur quitte la section
                if (timeoutId) clearTimeout(timeoutId);
              }
            });
          },
          { threshold: 0.3 }
        );

        observer.observe(pricingSection);
        observerCleanup = () => observer.disconnect();
      }
    } else if (variant === 'dashboard') {
      // Pour le dashboard, afficher après 5 secondes pour les nouveaux utilisateurs
      const hasSeenBubble = localStorage.getItem('dashboardChatBubbleSeen');
      if (!hasSeenBubble) {
        timeoutId = setTimeout(() => {
          setIsVisible(true);
          setHasTriggered(true);
        }, 5000);
        
        // Rotation des tips toutes les 5 secondes
        rotationInterval = setInterval(() => {
          setCurrentTip(prev => (prev + 1) % dashboardTips.length);
        }, 5000);
      }
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (observerCleanup) observerCleanup();
      if (rotationInterval) clearInterval(rotationInterval);
    };
  }, [variant, hasTriggered, isDismissed, dashboardTips.length]);

  const handleDismiss = () => {
    setIsVisible(false);
    setIsDismissed(true);
    sessionStorage.setItem('chatBubbleDismissed', 'true');
    if (variant === 'dashboard') {
      localStorage.setItem('dashboardChatBubbleSeen', 'true');
    }
  };

  const handleOpenChat = () => {
    setIsVisible(false);
    setIsDismissed(true);
    sessionStorage.setItem('chatBubbleDismissed', 'true');
    if (variant === 'dashboard') {
      localStorage.setItem('dashboardChatBubbleSeen', 'true');
    }
    onOpenChat();
  };

  if (isDismissed) return null;

  const currentTipData = dashboardTips[currentTip];
  const TipIcon = currentTipData?.icon || Bot;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.9 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          className="fixed bottom-24 right-4 z-50 max-w-sm"
          data-testid="proactive-chat-bubble"
        >
          <div className="relative">
            {/* Bulle principale */}
            <div 
              className="bg-gradient-to-br from-gray-900 to-gray-800 border border-cyan-400/30 rounded-2xl p-4 shadow-xl"
              style={{
                boxShadow: '0 0 20px rgba(0, 245, 255, 0.2), 0 10px 40px rgba(0, 0, 0, 0.5)'
              }}
            >
              {/* Bouton fermer */}
              <button
                onClick={handleDismiss}
                className="absolute top-2 right-2 p-1 text-gray-500 hover:text-white transition-colors"
                aria-label="Fermer"
              >
                <X className="w-4 h-4" />
              </button>

              {variant === 'landing' ? (
                // Version Landing Page
                <>
                  <div className="flex items-start gap-3">
                    <motion.div 
                      className="w-12 h-12 rounded-full bg-gradient-to-br from-cyan-400 to-magenta-400 flex items-center justify-center flex-shrink-0"
                      animate={{ 
                        scale: [1, 1.05, 1],
                        boxShadow: [
                          '0 0 10px rgba(0, 245, 255, 0.3)',
                          '0 0 20px rgba(0, 245, 255, 0.5)',
                          '0 0 10px rgba(0, 245, 255, 0.3)'
                        ]
                      }}
                      transition={{ duration: 2, repeat: Infinity }}
                    >
                      <Bot className="w-6 h-6 text-black" />
                    </motion.div>

                    <div className="flex-1 pr-4">
                      <p className="text-white text-sm font-medium mb-2">
                        Besoin d'aide pour votre{' '}
                        <span className="text-cyan-400 font-bold">déménagement</span> ou une{' '}
                        <span className="text-magenta-400 font-bold">succession</span> ?
                      </p>
                      <p className="text-gray-400 text-xs leading-relaxed">
                        Je connais toutes les démarches pour vous simplifier la vie. Posez-moi vos questions !
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 mt-3 mb-3">
                    <div className="flex items-center gap-1 px-2 py-1 bg-cyan-400/10 rounded text-xs text-cyan-400">
                      <Home className="w-3 h-3" />
                      <span>Déménagement</span>
                    </div>
                    <div className="flex items-center gap-1 px-2 py-1 bg-magenta-400/10 rounded text-xs text-magenta-400">
                      <Shield className="w-3 h-3" />
                      <span>Succession</span>
                    </div>
                  </div>
                </>
              ) : (
                // Version Dashboard avec tips rotatifs
                <>
                  <div className="flex items-start gap-3">
                    <motion.div 
                      className={`w-12 h-12 rounded-full ${currentTipData.bgColor} flex items-center justify-center flex-shrink-0`}
                      key={currentTip}
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ duration: 0.3 }}
                    >
                      <TipIcon className={`w-6 h-6 ${currentTipData.color}`} />
                    </motion.div>

                    <div className="flex-1 pr-4">
                      <motion.div
                        key={currentTip}
                        initial={{ opacity: 0, x: 10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <p className={`text-sm font-bold mb-1 ${currentTipData.color}`}>
                          {currentTipData.title}
                        </p>
                        <p className="text-gray-400 text-xs leading-relaxed">
                          {currentTipData.description}
                        </p>
                      </motion.div>
                    </div>
                  </div>

                  {/* Indicateurs de progression */}
                  <div className="flex items-center justify-center gap-1.5 mt-3 mb-2">
                    {dashboardTips.map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => setCurrentTip(idx)}
                        className={`w-2 h-2 rounded-full transition-all ${
                          idx === currentTip 
                            ? 'bg-cyan-400 w-4' 
                            : 'bg-gray-600 hover:bg-gray-500'
                        }`}
                      />
                    ))}
                  </div>

                  {/* Bouton spécial alertes si on est sur ce tip */}
                  {currentTip === 0 && (
                    <button
                      onClick={() => {
                        handleDismiss();
                        // Scroll vers la section alertes dans le sidebar
                        document.querySelector('[data-testid="alerts-toggle"]')?.scrollIntoView({ behavior: 'smooth' });
                      }}
                      className="w-full flex items-center justify-center gap-2 bg-yellow-400/20 border border-yellow-400/30 text-yellow-400 text-xs py-2 rounded-lg hover:bg-yellow-400/30 transition-all mb-2"
                    >
                      <Bell className="w-3 h-3" />
                      Activer les alertes maintenant
                      <ChevronRight className="w-3 h-3" />
                    </button>
                  )}
                </>
              )}

              {/* Bouton CTA */}
              <button
                onClick={handleOpenChat}
                className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-cyan-400 to-cyan-500 text-black font-orbitron font-bold text-sm py-3 rounded-lg hover:brightness-110 transition-all"
              >
                <MessageCircle className="w-4 h-4" />
                {variant === 'dashboard' ? 'Poser une question' : 'Discuter avec l\'Assistant'}
              </button>
            </div>

            {/* Flèche vers le bouton chat */}
            <div 
              className="absolute -bottom-2 right-8 w-4 h-4 bg-gray-800 border-r border-b border-cyan-400/30 transform rotate-45"
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export { ProactiveChatBubble };
export default ProactiveChatBubble;
